import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteRenderOptionState,
  FilterOptionsState,
  TextField,
  Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, {
  KeyboardEvent,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAppSelector, useShallowSelector } from '../../app/hooks';
import { ArrowKeys, Symbols } from '../../constants/enums';
import { orderActions } from '../../reducers/orderSlice';
import useOmniBarKeyboardActions from '../../hooks/useOmniBarKeyboardActions.hooks';
import {
  addCouponToCart,
  addLoyaltiesCode,
  applyLoyaltyCode,
  cartActions,
} from '../../reducers/cartSlice';
import {
  dialogActions,
  nextSlashCommand,
  NAVIGATION_LEVELS,
} from '../../reducers/dialogSlice';
import { addItemToCart } from '../../reducers/menuSlice';
import {
  AgentInterceptionType,
  InfoTransmissionMessage,
  messagingActions,
} from '../../reducers/messagingSlice';
import {
  selectDisableIntentInterval,
  selectFeatureFlagLoyaltySupport,
} from '../../redux/features/config/config.selector';
import {
  selectCartLoyalties,
  selectModGroupsWithDescriptions,
} from '../../selectors/cart';
import { selectDialog } from '../../selectors/dialog';
import useItemConversion from '../../hooks/useItemConversion.hooks';
import {
  generateMenuItemsSelector,
  getModSymbolMapping,
  posSettingsSelector,
  selectBMItemsSynonyms,
  selectIsBrandMenu,
  selectUnavailableItemsByName,
  selectTopLevelMenuItems,
} from '../../selectors/menu';
import { selectActiveRestaurantDetails } from '../../selectors/restaurant';
import { getItemUnavailableMessage } from '../../utils/TTSMessage';
import {
  findAnotherSelectedItem,
  getFilteredOptions,
  getOptionLabel,
  getQuantityFromInput,
  processQuantity,
} from '../../utils/autocomplete';
import Colors from '../../utils/color';
import {
  TTS_OFF_COMMAND,
  TTS_ON_COMMAND,
  SLASH_COMMAND_REMINDERS,
} from '../../utils/constants';
import {
  TopLevelMenuItem,
  isUnavailableItem,
  splitPathToItem,
} from '../../utils/menu';
import { AutocompleteOption } from './AutocompleteOption';
import useAutocompleteMessage from './hooks/useAutocompleteMessage';
import { checkEnterClicked, UpsellTriggers } from './omnibar.constant';
import { ParsedMenuItem } from '../../types/menu';
import { upsell } from '../../utils/upsell';
import { getMenuUnificationFeatureFlag } from '../../utils/session-storage';
import useModifierSelectionInfo from '../../hooks/useModifierSelectionInfo/useModifierSelectionInfo.hooks';
import {
  COUPON_LENGTH,
  LOYALTY_LENGTH,
  UPSELL,
  UPSELL_METRIC_VERSION,
  UPSELL_PREFIX,
} from '../../constants';
import useAIEscalationUpsell from '../../hooks/useAIEscalationUpsell.hooks';
import { MOD_CODES_GROUP_ID } from '../../utils/menuUnification.constants';
import { processModifierSelection, sortResults } from './util/helper';
import { pickNextIndex } from '../../utils/common';
import { isBMModifierAnUnavailableConstraint } from '../../utils/menuUnification.utils';
import useSelectedCartItems from '../../hooks/useSelectedItems/useSelectedCartItems.hooks';
import useModifierGroupNavigation from '../../hooks/useModifierGroupNavigation.hooks';
import { INTENT_CARDS } from '../../constants/intents';
import useReadback from '../../hooks/useReadback/useReadback.hooks';

const useStyles = makeStyles((theme: Theme) => ({
  wrapText: {
    width: '100%',
    borderRadius: theme.spacing(1),
    backgroundColor: Colors.white,
    padding: theme.spacing(1),
    '& input': {
      paddingLeft: `${theme.spacing(1)} !important`,
    },
    '& .MuiOutlinedInput-notchedOutline.MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
  },
  option: {
    pointerEvents: 'none',
  },
}));

interface IAutocompleteComp {
  disabled: boolean;
}

export const AutocompleteComp = ({ disabled }: IAutocompleteComp) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { handleModifierSelection } = useItemConversion();
  const {
    currentMessage,
    updateCurrentMessage,
    sendMessage,
    sendAgentInterventionEvent,
    sendSlashCommandMessage,
  } = useAutocompleteMessage();
  const { canUpsellInAIEscalation } = useAIEscalationUpsell();
  const menuItems = useSelector(generateMenuItemsSelector(currentMessage));
  const isBrandMenu = useSelector(selectIsBrandMenu);
  const bmItemsSynonyms = useSelector(selectBMItemsSynonyms);
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);
  const topLevelMenuItems = useSelector(selectTopLevelMenuItems);
  const cartLoyalties = useAppSelector(selectCartLoyalties);
  const handleLoyalty = useSelector(selectFeatureFlagLoyaltySupport);
  const isUnifiedMenuEnabled = getMenuUnificationFeatureFlag();

  const { cart, selectedCartItem, selectedItemCartId } = useSelectedCartItems();

  const dialogState = useAppSelector(selectDialog);
  const {
    steps: dialogSteps,
    step: dialogStep,
    slashCommands,
    slashCommandIndex,
    voiceBoardValue,
    intentsStatus,
    selectedModGroup,
    navigationLevel,
  } = dialogState;
  const { restaurantName } = useSelector(selectActiveRestaurantDetails);
  const posSettings = useSelector(posSettingsSelector);
  const [inputQuantity, setInputQuantity] = useState(1);
  const [inputModSymbol, setInputModSymbol] = useState('');
  const [downTime, setDownTime] = useState(0);
  const [hasPreviousMessage, setHasPreviousMessage] = useState(false);
  const modSymbolMapping = useShallowSelector(getModSymbolMapping);

  /*
    Loyalty Code can be:
      - Legacy loyalty is of 6 digit coupon code
      - Panera with loyalty is of 10 digits phone number (Controlled by feature flag)
  */
  const [loyaltyCode, setLoyaltyCode] = useState<string | null>(null);

  // Coupon Code
  const [couponCode, setCouponCode] = useState<string | null>(null);

  // Customer Name
  const [customerName, setCustomerName] = useState<string>('');

  const [prefixWord, setPrefixWord] = useState('');
  const [handleOutOfOrderSearch, setHandleOutOfOrderSearch] = useState(false);
  const { arrowDown, arrowLeft, arrowRight, arrowUp } = ArrowKeys;
  const { processCartItemSelectionKey } = useOmniBarKeyboardActions();
  const { handleReadBack } = useReadback();
  const { sendModifierSelectionInfo } = useModifierSelectionInfo();

  const { handleKeyDown: handleModifierNavigation } =
    useModifierGroupNavigation({
      selectedCartItem,
    });

  useEffect(() => {
    if (currentMessage) {
      setHasPreviousMessage(true);
    } else {
      let timer = setTimeout(() => {
        setHasPreviousMessage(false);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [currentMessage]);

  const modGroupsData = useSelector(selectModGroupsWithDescriptions);
  const {
    allModGroups: autoPromptModGroups,
    allDescriptions: autoPromptModGroupDescriptions,
  } = modGroupsData;

  const autoPromptModGroupDescriptionsToJSONString = JSON.stringify(
    autoPromptModGroupDescriptions
  );
  useEffect(() => {
    if (autoPromptModGroupDescriptions.length)
      dispatch(
        dialogActions.addInvalidModGroupDescriptions(
          autoPromptModGroupDescriptions
        )
      );
  }, [autoPromptModGroupDescriptionsToJSONString]);

  const getDialogMessage = (triggerUpsellAttempt: boolean = false) => {
    if (autoPromptModGroups.length && autoPromptModGroupDescriptions.length) {
      const firstAutoPromptModGroup = autoPromptModGroups[0];
      const firstAutoPromptDescription = autoPromptModGroupDescriptions[0];
      if (
        triggerUpsellAttempt &&
        isUnifiedMenuEnabled &&
        firstAutoPromptModGroup?.is_upsell_prompt
      ) {
        const upsell_attempted = upsell.attempt(
          firstAutoPromptModGroup,
          selectedCartItem?.name || '',
          UpsellTriggers.hitlOmnibar,
          firstAutoPromptModGroup.description || '.',
          firstAutoPromptModGroup.name
        );
        return {
          message: firstAutoPromptModGroup.description || '.',
          metadata: {
            upsell_attempted,
            upsell_metric_version: UPSELL_METRIC_VERSION,
          },
        };
      }
      return { message: firstAutoPromptDescription };
    } else {
      // If there are slash commands and we haven't processed all of them, use the current one
      if (
        slashCommands.length > 0 &&
        slashCommandIndex < slashCommands.length
      ) {
        return {
          message: slashCommands[slashCommandIndex].command,
        };
      }

      // Otherwise, use normal dialog steps logic
      const hasItemsInCart = Object.keys(cart.cartItems).length > 0;
      if (hasItemsInCart) {
        // When cart has items, use "Sure thing!" (index 1)
        return {
          message: dialogSteps[1].replaceAll(
            '{RESTAURANT_NAME}',
            restaurantName
          ),
        };
      }

      // Default: loop through dialog steps when cart is empty
      return {
        message: dialogSteps[dialogStep].replaceAll(
          '{RESTAURANT_NAME}',
          restaurantName
        ),
      };
    }
  };

  const DISABLE_INTENT_INTERVAL = useAppSelector(selectDisableIntentInterval);

  const handleKeyPress = (event: KeyboardEvent) => {
    const payload: Partial<InfoTransmissionMessage> = {
      data: { message: 'Key ' + event.key + ' pressed', type: 'METRIC' },
    };
    dispatch(messagingActions.sendInfo(payload as any));
    if (event.key === 'Enter') {
      event.preventDefault();
      !checkEnterClicked.getIsEnterClicked() && handleEnterPress(event);
    }
  };

  const handleEnterPress = (event: KeyboardEvent) => {
    if (loyaltyCode) {
      if (loyaltyCode.length <= COUPON_LENGTH) {
        // Call Apply Loyalty Code API
        dispatch(applyLoyaltyCode({ loyaltyCode }));
      } else {
        // For panera 10 digit phone number send loyalty code inside order event
        if (!cartLoyalties?.find(({ code }) => code === loyaltyCode)) {
          dispatch(addLoyaltiesCode({ code: loyaltyCode }));
        }
      }
    } else if (couponCode) {
      dispatch(addCouponToCart(couponCode.toString()));
      setCouponCode(null); // clear coupon state after adding to cart
    } else if (customerName) {
      // Set customer name in Redux state
      dispatch(orderActions.setCustomerName(customerName));
      setCustomerName(''); // clear customer name state after setting
      updateCurrentMessage('');
    } else if (!event.shiftKey) {
      if (voiceBoardValue !== '' && voiceBoardValue[0] === '/') {
        handleMessageSubmit();
      } else if (!event.shiftKey) {
        const { message, metadata } = getDialogMessage(true);
        sendMessage(message, metadata);

        // Handle dialog state advancement (same priority as getDialogMessage)
        if (
          autoPromptModGroups.length &&
          autoPromptModGroupDescriptions.length
        ) {
          // Auto-prompt mode - don't advance dialog state (auto-prompt handles its own flow)
        } else if (
          slashCommands.length > 0 &&
          slashCommandIndex < slashCommands.length
        ) {
          // Auto-add items to cart when their prompts are sent (only for DONATION and UPSELL_END_OF_ORDER)
          const currentSlashCommand = slashCommands[slashCommandIndex];

          // Use the intent from the slash command to check if it matches DONATION or UPSELL_END_OF_ORDER
          const intent = currentSlashCommand.intent;

          // Only trigger addItemToCart for DONATION (exact match) or UPSELL_END_OF_ORDER (prefix match)
          const shouldAddToCart =
            intent === SLASH_COMMAND_REMINDERS.DONATION ||
            intent.startsWith(
              SLASH_COMMAND_REMINDERS.UPSELL_END_OF_ORDER_PREFIX
            );

          if (shouldAddToCart) {
            // Find the corresponding top-level menu item by intent/name
            const matchingItem = Object.values(topLevelMenuItems).find(
              (item) =>
                !item.inactive &&
                (item.name === intent || item.name.startsWith(intent))
            );

            if (matchingItem) {
              dispatch(
                addItemToCart({
                  menuItem: matchingItem,
                  quantity: 1,
                  skipTTS: true, // TTS already be handled above so skip it in the thunk call
                })
              );
              sendAgentInterventionEvent(
                AgentInterceptionType.ORDER_CHANGED,
                `${matchingItem.name} added to cart`
              );
            }
          }

          // Advance to the next slash command
          dispatch(nextSlashCommand());
        } else {
          // Normal dialog steps mode
          if (dialogStep === dialogSteps.length - 1) {
            if (dialogStep !== 0) {
              dispatch(dialogActions.setStep(0));
            }
          } else {
            dispatch(dialogActions.increaseStep());
          }
        }
      }
    }
    checkEnterClicked.setIsEnterClicked(true);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    // Handle modifier group navigation first
    handleModifierNavigation(event);

    if ([arrowDown, arrowLeft, arrowRight, arrowUp, ' '].includes(event.key)) {
      if (event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (event.key !== ' ' && !downTime) setDownTime(new Date().valueOf()); // Arrow keys use key press duration to select the TTS message
    }
  };

  const sendTTSByUsingArrowKeys = (event: KeyboardEvent) => {
    let upTime = new Date().valueOf();
    const timePressed = Math.floor((upTime - downTime) / 100);
    const getMessage = (card: number) => {
      const messages = INTENT_CARDS[card].messages;
      const messageIndex = pickNextIndex(
        messages.length,
        intentsStatus[card]?.lastMessageIndex
      );
      let message = '';
      if (messageIndex !== undefined) {
        message = messages[messageIndex];
        dispatch(
          dialogActions.setIntentLastMessageIdx({
            idx: card,
            lastMessageIndex: messageIndex,
          })
        );
      }
      return message;
    };

    let intentIndex: number | null = null;

    switch (event.key) {
      case arrowUp:
        intentIndex = 1; // #okay
        break;
      case arrowLeft:
        intentIndex = 3; // #moment
        break;
      case arrowRight:
        intentIndex = 5; // #continue
        break;
      case arrowDown:
        intentIndex = 7; // #readback
        break;
      default:
        break;
    }

    if (
      intentIndex !== null &&
      intentsStatus[intentIndex] &&
      intentsStatus[intentIndex].clickable
    ) {
      if (intentIndex === 7) {
        handleReadBack({ timePressed });
        sendAgentInterventionEvent(AgentInterceptionType.TTS_SENT);
      } else {
        sendMessage(getMessage(intentIndex));
      }
      dispatch(
        dialogActions.setIntentClickable({ idx: intentIndex, status: false })
      );
      setTimeout(() => {
        if (intentIndex)
          dispatch(
            dialogActions.setIntentClickable({ idx: intentIndex, status: true })
          );
      }, DISABLE_INTENT_INTERVAL);
    }

    setDownTime(0);
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    if (!currentMessage) {
      event.preventDefault();
      if (
        Object.values(ArrowKeys).includes(event.key as ArrowKeys) &&
        !event.shiftKey
      )
        sendTTSByUsingArrowKeys(event); //click arrow keys when there is nothing typed on Omnibar
    }

    if (event.shiftKey) {
      event.preventDefault();
      // Use Shift+Backspace key for removing last cart item
      if (!currentMessage && !hasPreviousMessage) {
        if (
          event.key === 'Backspace' &&
          selectedCartItem &&
          selectedItemCartId > 0
        ) {
          dispatch(cartActions.deleteCartItem(selectedItemCartId.toString()));
          sendAgentInterventionEvent(
            AgentInterceptionType.ORDER_CHANGED,
            `${selectedCartItem.name} removed from order`
          );

          const newCartItemId = findAnotherSelectedItem(
            cart.cartItems,
            selectedItemCartId
          );
          if (newCartItemId > 0) {
            dispatch(
              dialogActions.selectItemAndResetNavigation({
                item: cart.cartItems[newCartItemId.toString()],
                itemCartId: newCartItemId,
              })
            );
          } else {
            dispatch(dialogActions.selectItemAndResetNavigation(undefined));
          }

          if (isUnifiedMenuEnabled) {
            // Check upsell and auto reject it if the cart context changes
            upsell.settle();
          }
        }
        if (
          [ArrowKeys.arrowDown, ArrowKeys.arrowUp].includes(event.key as any)
        ) {
          processCartItemSelectionKey(event.key);
        }
      }
    }
    checkEnterClicked.setIsEnterClicked(false);
  };

  const handleMessageSubmit = () => {
    if (voiceBoardValue.length > 0) {
      //Send message only when the agent typed '//ttson' for TTS-ON and '/\ttsoff' for TTS-OFF
      if (voiceBoardValue === TTS_OFF_COMMAND) {
        sendMessage('\\'); // The backslash ("\") character is a special escape character. To use backslash character itself, two backslashes are needed.
      }

      if (voiceBoardValue === TTS_ON_COMMAND) {
        sendMessage('/');
      }

      updateCurrentMessage('');
      dispatch(dialogActions.setVoiceBoardValue(''));
    }
  };

  const onTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const curValue: any = event.currentTarget.value;

    // Agent Interception TYPING event
    if (!currentMessage && curValue) {
      sendAgentInterventionEvent(AgentInterceptionType.TYPING);
    }

    if (
      curValue &&
      [
        Symbols.star,
        Symbols.plus,
        Symbols.minus,
        Symbols.caret,
        Symbols.at,
      ].includes(curValue[0])
    ) {
      const firstChar = curValue[0];
      if ([Symbols.star, Symbols.caret].includes(firstChar)) {
        //Logic related to coupon code
        const couponNum = curValue.slice(1);
        if (!isNaN(couponNum)) {
          if (
            firstChar === Symbols.star &&
            couponNum.length <= (handleLoyalty ? LOYALTY_LENGTH : COUPON_LENGTH)
          ) {
            setLoyaltyCode(couponNum);
            if (couponCode) setCouponCode(null);
            if (customerName) setCustomerName('');
            updateCurrentMessage(Symbols.star + couponNum);
          } else if (firstChar === Symbols.caret) {
            // use caret key: ^ to enter coupon besides loyalty
            setCouponCode(couponNum);
            if (loyaltyCode) setLoyaltyCode(null);
            if (customerName) setCustomerName('');
            updateCurrentMessage(Symbols.caret + couponNum);
          }
        }
      } else if (firstChar === Symbols.at) {
        // use @ symbol to enter customer name
        const name = curValue.slice(1);
        setCustomerName(name);
        if (loyaltyCode) setLoyaltyCode(null);
        if (couponCode) setCouponCode(null);
        updateCurrentMessage(Symbols.at + name);
      } else if ([Symbols.plus, Symbols.minus].includes(firstChar)) {
        updateCurrentMessage(curValue);
      }
    } else {
      updateCurrentMessage(curValue);
      setLoyaltyCode(null);
      setCouponCode(null);
      setCustomerName('');
      dispatch(dialogActions.setVoiceBoardValue(curValue));
    }
  };

  const setCurrentMessageAndVoiceBoardValue = (
    message = '',
    voiceBoardValue = ''
  ) => {
    updateCurrentMessage(message);
    dispatch(dialogActions.setVoiceBoardValue(voiceBoardValue));
  };

  const onSelectChange = (
    event: any,
    value: ParsedMenuItem | string | TopLevelMenuItem | null,
    reason: AutocompleteChangeReason
  ) => {
    if (value === null) {
      setCurrentMessageAndVoiceBoardValue();
    } else if (typeof value === 'string') {
      setCurrentMessageAndVoiceBoardValue(value, value);
    } else if (value instanceof Object) {
      // Finding whether item is unavailable (or) not based on brand menu
      const getBMUnavailability = () => {
        if (value.pathToItem && selectedCartItem) {
          const modifierTags = splitPathToItem(value.pathToItem);
          const parentModifier =
            modifierTags.length > 1
              ? modifierTags[modifierTags.length - 2]
              : selectedCartItem?.name || '';
          if (
            isBMModifierAnUnavailableConstraint(
              selectedCartItem,
              unavailableItemsByName,
              parentModifier,
              value.pathToItem,
              value.parentModifierGroupDisplayName ||
                value.parentModifierGroupName ||
                selectedCartItem?.name ||
                '',
              value.name
            )
          )
            return true;
        }
      };

      const isOptionUnavailable = isUnavailableItem(value);
      const isUnavailable = isUnifiedMenuEnabled
        ? !value?.available || isOptionUnavailable || getBMUnavailability()
        : isOptionUnavailable;

      if (isUnavailable) {
        if (!value.name.startsWith('/'))
          sendMessage(getItemUnavailableMessage(value.name));
        return;
      }

      if (canUpsellInAIEscalation(value)) {
        return;
      }

      if (
        !value.name.includes('/upsell') &&
        (value.category === 'tts-prompt' || value.category.startsWith('/'))
      ) {
        sendSlashCommandMessage(value, isUnifiedMenuEnabled);
        setCurrentMessageAndVoiceBoardValue();
      } else if (value.category === 'modifier') {
        setCurrentMessageAndVoiceBoardValue();
        if (selectedCartItem && selectedItemCartId) {
          const menuItem = value as ParsedMenuItem;
          let { isChecked, modGroup } = processModifierSelection({
            cartItem: cart.cartItems[selectedItemCartId],
            menuItem,
            modSymbolMapping,
            selectedModGroup,
            inputModSymbol,
            navigationLevel,
            parentModGroupId: dialogState.parentModGroupId,
          });

          if (modGroup) {
            // Declined removes selected upsell item
            if (
              menuItem.name.toLocaleLowerCase() === 'declined' ||
              (menuItem.name.toLocaleLowerCase() === 'decline' &&
                (selectedCartItem.name.startsWith(UPSELL_PREFIX) ||
                  selectedCartItem.name.startsWith(UPSELL)))
            ) {
              isChecked = true;
              dispatch(
                cartActions.deleteCartItem(selectedItemCartId.toString())
              );
              sendAgentInterventionEvent(
                AgentInterceptionType.ORDER_CHANGED,
                `${selectedCartItem.name} removed from order`
              );
              const newCartItemId = findAnotherSelectedItem(
                cart.cartItems,
                selectedItemCartId
              );
              if (newCartItemId > 0) {
                dispatch(
                  dialogActions.selectItemAndResetNavigation({
                    item: cart.cartItems[newCartItemId.toString()],
                    itemCartId: newCartItemId,
                  })
                );
              } else {
                dispatch(dialogActions.selectItemAndResetNavigation(undefined));
              }
            } else {
              dispatch(
                cartActions.selectModifier({
                  cartItemId: selectedItemCartId,
                  menuItemId: menuItem.itemId,
                  modGroupId: modGroup.id,
                  selected: isChecked,
                  modCode: inputModSymbol,
                  modSymbolMapping,
                  rootModGroupId:
                    navigationLevel === NAVIGATION_LEVELS.TOP
                      ? selectedModGroup?.id
                      : '',
                  posSettings,
                  pathToItem: menuItem.pathToItem,
                  readback: true,
                })
              );

              if (
                isBrandMenu &&
                inputModSymbol &&
                modSymbolMapping[inputModSymbol]
              ) {
                const modCodesGroupId = Object.keys(
                  menuItem.modifierGroups || {}
                ).filter((entry) =>
                  entry.toLocaleLowerCase().startsWith(MOD_CODES_GROUP_ID)
                )?.[0];

                if (modCodesGroupId) {
                  dispatch(
                    cartActions.selectModifier({
                      cartItemId: selectedItemCartId,
                      menuItemId: modSymbolMapping[inputModSymbol].name,
                      modGroupId: modCodesGroupId,
                      selected: true,
                      modCode: '',
                      modSymbolMapping,
                      rootModGroupId: selectedModGroup?.id,
                      pathToItem: `${menuItem.pathToItem}__${menuItem.itemId}__${modCodesGroupId}`,
                      posSettings,
                      readback: true,
                      bmItemsSynonyms,
                    })
                  );
                }
              }

              // Check for item conversion and option replacement after processing all modifier selection logic
              // This ensures the agent intervention event is sent for the conversion size selection
              handleModifierSelection(
                modGroup,
                menuItem.name,
                selectedCartItem
              );
            }
            sendModifierSelectionInfo({
              menuItem,
              modGroup: modGroup,
              selected: isChecked,
            });
          }
          dispatch(dialogActions.setHighlightedModifier(''));
        }
      } else {
        if (value.name.includes('/upsell') && !isUnifiedMenuEnabled) {
          sendSlashCommandMessage(value, isUnifiedMenuEnabled);
          updateCurrentMessage('');
        }
        dispatch(dialogActions.setVoiceBoardValue(''));

        const quantity = processQuantity(inputQuantity, value, currentMessage);
        // note: mod symbol never used on root items
        dispatch(
          addItemToCart({
            menuItem: value,
            prefixWord,
            inputModSymbol,
            quantity,
          })
        );
        sendAgentInterventionEvent(
          AgentInterceptionType.ORDER_CHANGED,
          `${value.name} added to cart`
        );

        setInputQuantity(1);
        updateCurrentMessage('');
        setPrefixWord('');
        setHandleOutOfOrderSearch(false);
      }
    }
  };

  const getPlaceHolder = () => getDialogMessage(false).message;

  const filterOptions = (
    options: (TopLevelMenuItem | ParsedMenuItem)[],
    state: FilterOptionsState<TopLevelMenuItem | ParsedMenuItem>
  ) => {
    const { quantity, inputValue, modSymbol } = getQuantityFromInput(
      state.inputValue,
      modSymbolMapping
    );

    setInputQuantity(quantity || 1);
    setInputModSymbol(modSymbol ? modSymbol : '');

    let resultList = getFilteredOptions(
      modSymbol,
      inputValue,
      quantity,
      options,
      state.inputValue
    );

    // Search for the second word if no result found for the whole input value and only hanlde two words for now. i.e. coke large
    // Also, if the input value starts with @ (which is used for customer name), then don't search for the second word
    const splittedInputValue = inputValue.trim().split(' ');
    if (
      !Object.values(resultList).length &&
      splittedInputValue.length === 2 &&
      !inputValue.startsWith(Symbols.at)
    ) {
      resultList = getFilteredOptions(
        modSymbol,
        splittedInputValue[1],
        quantity,
        options,
        state.inputValue
      );
      setPrefixWord(splittedInputValue[0]);
      setHandleOutOfOrderSearch(true);
    }

    return sortResults(resultList, isUnifiedMenuEnabled);
  };

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    menuItem: ParsedMenuItem | TopLevelMenuItem,
    state: AutocompleteRenderOptionState
  ) => {
    const optionProps = {
      htmlElemProps: props,
      menuItem,
      state,
      handleOutOfOrderSearch,
      canUpsellInAIEscalation,
      inputModSymbol,
    };
    return (
      <AutocompleteOption
        key={`${menuItem.id}${
          menuItem.categoryId ||
          (menuItem as ParsedMenuItem)?.parentModifierGroupId
        }`}
        {...optionProps}
      />
    );
  };

  const handleHighlightChange = (
    event: SyntheticEvent<Element, Event>,
    value: ParsedMenuItem | string | TopLevelMenuItem | null
  ) => {
    if (value instanceof Object && value.category === 'modifier') {
      const modifier = value as ParsedMenuItem;
      dispatch(dialogActions.setHighlightedModifier(modifier.pathToItem || ''));
    } else {
      dispatch(dialogActions.setHighlightedModifier(''));
    }
  };

  return (
    <Autocomplete
      data-testid="omnibar-autocomplete"
      freeSolo
      fullWidth
      clearOnEscape
      open={currentMessage !== ''}
      autoHighlight={true}
      value={null}
      inputValue={currentMessage}
      onChange={onSelectChange}
      onHighlightChange={handleHighlightChange}
      options={menuItems}
      getOptionLabel={getOptionLabel}
      onClose={() => updateCurrentMessage('')}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={(input) => input && input.focus()}
          className={classes.wrapText}
          autoFocus
          id="omnitext"
          placeholder={getPlaceHolder()}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          value={currentMessage}
          onChange={onTextChange}
          disabled={disabled}
          InputProps={{ ...params.InputProps, endAdornment: null }}
        />
      )}
      filterOptions={filterOptions}
      renderOption={renderOption}
    />
  );
};
